<template>
  <validation-provider
    :default-values="defaultValues"
    :default-errors="serverErrors"
    :resolver="resolver"
    instant-validate
    @submit="onSubmit"
  >
    <template #default="{ submitted, handleSubmit }">
      <form-layout
        ref="form"
        gap-before="100px"
        action="/demo-create"
        method="post"
        :class="$style.form"
        data-qa="demo-request-form"
        @submit="handleSubmit"
      >
        <h1>{{ i18n('title') }}</h1>
        <fields-layout
          :class="$style.container"
          gap="20px"
        >
          <validation-field
            hidden
            name="_xsrf"
          />
          <validation-field
            hidden
            name="code"
          />
          <validation-field
            hidden
            name="type"
          />

          <registration-field
            :placeholder="i18n('name')"
            field="name"
            :submitted="submitted"
            autofocus
          />
          <registration-field
            :placeholder="i18n('username')"
            :submitted="submitted"
            field="username"
          />
          <registration-field
            :placeholder="i18n('position')"
            :submitted="submitted"
            field="position"
          />
          <registration-field
            :placeholder="i18n('phone')"
            type="tel"
            field="phone"
            :submitted="submitted"
            show-only-one-error
          />
          <registration-field
            :placeholder="i18n('email')"
            type="email"
            field="email"
            :submitted="submitted"
            show-only-one-error
          >
            <template #error="{ message }">
              <template v-if="message === 'error.need_login'">
                {{ i18n('need-login') }}
                <a :href="loginLink">{{ i18n('need-login.login') }}</a>
              </template>
              <template v-else>
                {{ message }}
              </template>
            </template>
          </registration-field>

          <validation-field
            name="locale"
            :class="$style.selectContainer"
            :hidden="!showLangSelect"
          >
            <template #default="{ id, modelValue, invalid, onChange, name }">
              <base-select-dropdown
                :id="id"
                :class="[$style.select, { [$style.invalid]: invalid && submitted }]"
                :items="localeItems"
                :model-value="modelValue"
                show-checkbox
                :name="name"
                @update:model-value="onChange"
              />
            </template>
            <template #error="{ error }">
              <span :class="$style.error">
                {{ error.message }}
              </span>
            </template>
          </validation-field>
          <validation-field
            name="timezone"
            :class="$style.selectContainer"
          >
            <template #default="{ id, modelValue, invalid, onChange, name }">
              <base-select-dropdown
                :id="id"
                :class="[$style.select, { [$style.invalid]: invalid && submitted }]"
                :items="timezonesQuery.data.items"
                :model-value="modelValue"
                show-checkbox
                :name="name"
                @update:model-value="onChange"
              />
            </template>
            <template #error="{ error }">
              <span :class="$style.error">
                {{ error.message }}
              </span>
            </template>
          </validation-field>
          <base-button
            :class="$style.button"
            name="create"
            type="submit"
            data-qa="save"
          >
            {{ i18n('submit') }}
          </base-button>
          <validation-errors
            name="common"
            :all="false"
          >
            <template #default="{ message }">
              <div :class="$style.error">
                <template v-if="message === 'error.need_logout'">
                  {{ i18n('need-logout') }}
                  <br>
                  <a
                    :href="loginLink"
                    target="_blank"
                  >
                    <strong>{{ i18n('need-logout.goto') }}</strong>
                  </a>
                  &nbsp;·&nbsp;
                  <a
                    href="/account/logout"
                    target="_blank"
                  >
                    <strong>{{ i18n('logout') }} </strong></a>
                </template>
                <template v-else>
                  {{ message }}
                </template>
              </div>
            </template>
          </validation-errors>
          <p
            :class="$style.privacyPolicy"
            v-html="i18n('consent') + privacyPolicyLink"
          />
        </fields-layout>
      </form-layout>
    </template>
  </validation-provider>
</template>

<script setup lang="ts">
import { object, string } from 'yup';
import { reactive, useTemplateRef } from 'vue';
import RegistrationField from '@/modules/registration/components/RegistrationField.vue';
import { useTimezonesQuery } from '@/entities/staged/timezone';
import BaseButton from '@/shared/legacy/ui/button/button.vue';
import { appConfig } from '@/shared/legacy/lib/config/app-config';
import { DateTimeHelper } from '@/shared/legacy/lib/util/date-time-helper';
import { XSRFHelper } from '@/shared/legacy/lib/util/xsrf-helper';
import FieldsLayout from '@/shared/legacy/ui/fields-layout/fields-layout.vue';
import FormLayout from '@/shared/legacy/ui/form-layout/form-layout.vue';
import {
  ValidationField,
  ValidationErrors,
  ValidationProvider,
  yupResolver
} from '@/shared/legacy/ui/validate-form';
import { useI18N } from '@/shared/legacy/lib/use-i18n';
import BaseSelectDropdown from '@/shared/ui/base-list/base-select-dropdown.vue';

const { fields, serverErrors } = defineProps<{
  fields: any;
  serverErrors: any;
}>();

const i18n = useI18N();

const lang = appConfig.get('lang');
const instanceRegion = appConfig.get('instanceRegion');

const localeItems = [
  {
    id: 'ru_RU',
    name: i18n('language.ru')
  },
  {
    id: 'en_US',
    name: i18n('language.en')
  }
];
const defaultLocale = lang !== 'ru' || instanceRegion === 'AI' ? 'en_US' : 'ru_RU';
const defaultValues = {
  name: '',
  username: '',
  position: '',
  phone: '',
  email: '',
  _xsrf: XSRFHelper.getToken(),
  ...fields,
  type: 'internal',
  locale: defaultLocale,
  timezone: lang === 'ru' ? DateTimeHelper.browserTimezone : 'Europe/Berlin'
};

const timezonesQuery = reactive(useTimezonesQuery());

const resolver = yupResolver(
  object({
    name: string().required(),
    username: string().required(),
    position: string().required(),
    phone: string().required().min(7),
    email: string().required().email(),
    locale: string().required(),
    timezone: string().required()
  })
);

const showLangSelect = instanceRegion !== 'AI';
const privacyPolicyLink = `<a href="/docs/privacy-policy" target="_blank">${i18n('consent.link')}</a>`;
const loginLink = `/account/login?backurl=${encodeURIComponent(`/demo-create?code=${fields.code}`)}`;

const form = useTemplateRef<HTMLFormElement>('form');

const onSubmit = () => {
  if (form.value) {
    form.value.$el.submit();
  }
};
</script>

<style module>
.form {
  align-items: center;

  h1 {
    font-weight: 500;
    font-size: 45px;
    line-height: 50px;
    text-align: center;
    margin: 0 0 40px;
    color: #16bf54;
  }
  a:hover {
    color: #16bf54;
  }
}

.container {
  max-width: 450px;
  width: 100%;
}

.select {
  height: 46px;
  font-size: 20px;
  transition: all 300ms;
  background: $whiteColor;
  font-family: Graphik, Arial, Tahoma, Verdana, sans-serif;
  outline: none;
  border: 1px solid $grayLight;
  width: 100%;
  border-radius: 3px;
  appearance: none;
  &:focus-within {
    border: 1px solid #16bf54;
    box-shadow: 0 0 10px 0 rgba(22, 191, 84, 0.2);
  }
  @media (max-width: 768px) {
    height: 35px;
    padding: 0 15px;
  }
}

.selectContainer {
  position: relative;
}

.button {
  border-radius: 3px;
  transition: all 300ms;
  white-space: nowrap;
  text-decoration: none;
  width: 100%;
  height: 74px;
  font-size: 22px;
  line-height: 28px;
  color: $whiteColor;
  background-color: #16bf54;
  justify-content: center;

  @media (max-width: 768px) {
    height: 44px;
    font-size: 16px;
    line-height: 22px;
  }

  &_full-size {
    flex-basis: 100%;
  }
  &_center {
    justify-content: center;
  }
}

.invalid {
  border: 1px solid $red-400;
}

.error {
  margin-top: -20px;
  font-size: 18px;
  color: $red-400;
  line-height: 25px;
  a {
    color: $red-400;
  }
}

.privacyPolicy {
  color: #8f999c;
  font-size: 16px;
  line-height: 22px;
  a {
    color: #8f999c;
  }
}
</style>

<i18n lang="json">
{
  "consent": {
    "ru_RU": "Отправляя форму, я&nbsp;даю согласие на&nbsp;обработку перс. данных в&nbsp;соответствии с&nbsp;",
    "en_US": "By submitting this form, I consent to the processing of my personal data in accordance with Huntflow's"
  },
  "consent.link": {
    "ru_RU": "Политикой&nbspконфиденциальности",
    "en_US": "privacy&nbsppolicy"
  },
  "logout": {
    "ru_RU": "Выйти",
    "en_US": "Log out"
  },
  "need-logout": {
    "ru_RU": "Для получения нового демо-доступа необходимо выйти из вашего текущего Хантфлоу.",
    "en_US": "To get a new trial, you need to log out of your current Huntflow account."
  },
  "need-logout.goto": {
    "ru_RU": "Перейти в ваш Хантфлоу",
    "en_US": "Go to your Huntflow"
  },
  "submit": {
    "ru_RU": "Получить демо-доступ",
    "en_US": "Get a demo"
  },
  "title": {
    "ru_RU": "Демо-доступ к Хантфлоу",
    "en_US": "Huntflow Demo"
  },
  "timezone": {
    "ru_RU": "Часовой пояс",
    "en_US": "Timezone"
  },
  "language": {
    "ru_RU": "Язык интерфейса",
    "en_US": "Interface language"
  },
  "language.ru": {
    "ru_RU": "Русский язык интерфейса",
    "en_US": "Russian"
  },
  "language.en": {
    "ru_RU": "Английский язык интерфейса",
    "en_US": "English"
  },
  "name": {
    "ru_RU": "Название вашей организации",
    "en_US": "Company name"
  },
  "username": {
    "ru_RU": "Ваше имя",
    "en_US": "Full name"
  },
  "position": {
    "ru_RU": "Ваша должность",
    "en_US": "Job title"
  },
  "phone": {
    "ru_RU": "Ваш телефон для связи",
    "en_US": "Contact phone number"
  },
  "email": {
    "ru_RU": "Ваша эл. почта",
    "en_US": "Email"
  },
  "need-login": {
    "ru_RU": "Эл. почта уже зарегистрирована.",
    "en_US": "Sorry, this email is already registered."
  },
  "need-login.login": {
    "ru_RU": "Войти в Хантфлоу",
    "en_US": "Log in"
  }
}
</i18n>
